import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, Lock } from "lucide-react";
import { api, errMsg, imgUrl, CATEGORY_LABELS, CONDITION_LABELS } from "@/lib/api";
import { ZipInput } from "@/components/ZipInput";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle } from "@/components/States";
import { inputCls, areaCls, btnPrimary, chip, card, label } from "@/lib/ui";

const PRESET_WINDOWS = ["Weekday mornings", "Weekday evenings", "Sat 10am–2pm", "Sun afternoons"];
const EMPTY = { title: "", description: "", category: "", condition: "", price: "", zip: "", area: "", price_type: "offers", min_offer: "", pickup_windows: [], transport_notes: "", photos: [], vehicle: null };
const EMPTY_VEHICLE = { make: "", model: "", year: "", mileage: "", test_drive: false };

const VehicleFields = ({ v, onChange }) => (
  <div className="grid grid-cols-2 gap-3" data-testid="vehicle-fields">
    {[["make", "Make", "text", "e.g. Toyota"], ["model", "Model", "text", "e.g. Camry"], ["year", "Year", "number", "e.g. 2015"], ["mileage", "Mileage", "number", "e.g. 82000"]].map(([k, t, type, ph]) => (
      <div key={k}><span className={label}>{t}</span>
        <input data-testid={`vehicle-${k}-input`} required type={type} min={type === "number" ? (k === "year" ? 1900 : 0) : undefined} max={k === "year" ? 2100 : undefined} maxLength={40} placeholder={ph} value={v[k]} onChange={(e) => onChange({ ...v, [k]: e.target.value })} className={inputCls} />
      </div>
    ))}
    <label className="col-span-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
      <input data-testid="vehicle-testdrive-checkbox" type="checkbox" className="h-4 w-4 accent-blue-600" checked={!!v.test_drive} onChange={(e) => onChange({ ...v, test_drive: e.target.checked })} />
      Test drive welcome
    </label>
  </div>
);

const Photos = ({ photos, setPhotos }) => {
  const ref = useRef();
  const [uploading, setUploading] = useState(0);
  const onFiles = async (files) => {
    for (const file of Array.from(files).slice(0, 8 - photos.length)) {
      if (!/image\/(jpeg|png|webp)/.test(file.type)) { toast.error(`${file.name}: only JPEG, PNG or WebP`); continue; }
      if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name}: must be under 8 MB`); continue; }
      setUploading((n) => n + 1);
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads", fd);
        setPhotos((p) => [...p, data.path]);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {photos.map((p, i) => (
        <div key={p} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100">
          <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
          {i === 0 && <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-white/90 px-1.5 rounded">Cover</span>}
          <button type="button" data-testid={`photo-remove-${i}`} onClick={() => setPhotos((ps) => ps.filter((x) => x !== p))} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-white/90 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>
      ))}
      {photos.length < 8 && (
        <button type="button" data-testid="photo-upload-btn" onClick={() => ref.current.click()} className="aspect-square rounded-xl border-2 border-dashed border-slate-300 hover:border-blue-500 hover:bg-blue-50/50 flex flex-col items-center justify-center text-slate-500 text-xs font-semibold gap-1 transition-colors">
          {uploading ? <Loader2 className="h-6 w-6 animate-spin text-blue-600" /> : <ImagePlus className="h-6 w-6" />}
          {uploading ? "Uploading…" : "Add photos"}
        </button>
      )}
      <input ref={ref} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
    </div>
  );
};

export default function Sell() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ ...EMPTY, zip: user?.zip || "91401" });
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(!!id);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (!id && user?.zip) setF((prev) => ({ ...prev, zip: user.zip }));
  }, [id, user?.zip]);

  useEffect(() => {
    if (id) {
      api.get(`/listings/${id}`).then(({ data }) => {
        setF({ ...EMPTY, ...data, vehicle: data.vehicle ? { ...data.vehicle, year: String(data.vehicle.year), mileage: String(data.vehicle.mileage) } : null, min_offer: data.min_offer ?? "", transport_notes: data.transport_notes || "", price: String(data.price) });
        setLoading(false);
      }).catch((e) => { toast.error(errMsg(e)); nav("/"); });
    }
  }, [id, nav]);

  const toggleWindow = (w) => setF({ ...f, pickup_windows: f.pickup_windows.includes(w) ? f.pickup_windows.filter((x) => x !== w) : [...f.pickup_windows, w] });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.photos.length) return toast.error("Add at least one photo.");
    if (!f.category || !f.condition) return toast.error("Pick a category and condition.");
    setBusy(true);
    const body = { ...f, price: Number(f.price), min_offer: f.price_type === "offers" && f.min_offer !== "" ? Number(f.min_offer) : null, transport_notes: f.transport_notes || null, area: f.area || null,
      vehicle: f.category === "vehicles" && f.vehicle ? { ...f.vehicle, year: Number(f.vehicle.year), mileage: Number(f.vehicle.mileage) } : null };
    try {
      const { data } = id ? await api.put(`/listings/${id}`, body) : await api.post("/listings", body);
      toast.success(id ? "Listing updated" : "Your item is live");
      nav(`/listing/${data.id}`);
    } catch (e2) {
      toast.error(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;
  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto space-y-5" data-testid="listing-form">
      <PageTitle sub="A few details now saves a lot of messages later.">{id ? "Edit listing" : "Sell an item"}</PageTitle>
      <section className={card + " space-y-3"}>
        <span className={label}>Photos (up to 8)</span>
        <Photos photos={f.photos} setPhotos={(fn) => setF((prev) => ({ ...prev, photos: typeof fn === "function" ? fn(prev.photos) : fn }))} />
      </section>
      <section className={card + " space-y-4"}>
        <div><span className={label}>Title</span><input data-testid="listing-title-input" required minLength={3} maxLength={90} value={f.title} onChange={set("title")} placeholder="e.g. Oak dining table, seats 6" className={inputCls} /></div>
        <div>
          <span className={label}>Category</span>
          <div className="flex flex-wrap gap-2">{Object.entries(CATEGORY_LABELS).map(([k, v]) => <button type="button" key={k} data-testid={`listing-category-${k}`} className={chip(f.category === k)} onClick={() => setF({ ...f, category: k, vehicle: k === "vehicles" ? f.vehicle || EMPTY_VEHICLE : null })}>{v}</button>)}</div>
        </div>
        {f.category === "vehicles" && f.vehicle && <VehicleFields v={f.vehicle} onChange={(vehicle) => setF({ ...f, vehicle })} />}
        <div>
          <span className={label}>Condition</span>
          <div className="flex flex-wrap gap-2">{Object.entries(CONDITION_LABELS).map(([k, v]) => <button type="button" key={k} data-testid={`listing-condition-${k}`} className={chip(f.condition === k)} onClick={() => setF({ ...f, condition: k })}>{v}</button>)}</div>
        </div>
        <div><span className={label}>Description</span><textarea data-testid="listing-description-input" required minLength={10} rows={4} value={f.description} onChange={set("description")} placeholder="Size, flaws, what's included. Honest details prevent no-sale pickups." className={areaCls} /></div>
      </section>
      <section className={card + " space-y-4"}>
        <div className="grid grid-cols-2 gap-3">
          <div><span className={label}>Price</span><input data-testid="listing-price-input" required type="number" min="0" value={f.price} onChange={set("price")} className={inputCls + " font-bold"} /></div>
          <div>
            <span className={label}>Pricing</span>
            <div className="flex gap-1 p-1 rounded-xl bg-slate-100 h-11">
              {[["offers", "Offers OK"], ["firm", "Firm"]].map(([k, v]) => (
                <button type="button" key={k} data-testid={`listing-pricetype-${k}`} onClick={() => setF({ ...f, price_type: k })} className={`flex-1 rounded-lg text-sm font-semibold ${f.price_type === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>{v}</button>
              ))}
            </div>
          </div>
        </div>
        {f.price_type === "offers" && (
          <div>
            <span className={label + " flex items-center gap-1"}><Lock className="h-3 w-3" /> Private minimum offer (optional)</span>
            <input data-testid="listing-minoffer-input" type="number" min="0" value={f.min_offer} onChange={set("min_offer")} placeholder="Offers below this get a polite automatic decline" className={inputCls} />
            <p className="text-xs text-slate-500 mt-1">Never shown to buyers.</p>
          </div>
        )}
      </section>
      <section className={card + " space-y-4"}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <span className={label}>ZIP code</span>
            <ZipInput testid="listing-zip-input" value={f.zip} onChange={(zip) => setF({ ...f, zip })} />
          </div>
          <div><span className={label}>Approximate pickup area (optional)</span><input data-testid="listing-area-input" value={f.area} onChange={set("area")} placeholder="e.g. Near Main St & 5th Ave" className={inputCls} /></div>
        </div>
        <p className="text-xs text-slate-500">Don't enter your home address — exact meeting details are shared only with a confirmed buyer.</p>
        <div>
          <span className={label}>When can buyers pick up?</span>
          <div className="flex flex-wrap gap-2">
            {[...new Set([...PRESET_WINDOWS, ...f.pickup_windows])].map((w) => <button type="button" key={w} data-testid={`listing-window-${w}`} className={chip(f.pickup_windows.includes(w))} onClick={() => toggleWindow(w)}>{w}</button>)}
          </div>
          <div className="flex gap-2 mt-2">
            <input data-testid="listing-window-custom" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Add your own window" className={inputCls} />
            <button type="button" data-testid="listing-window-add" className={chip(false) + " h-11"} onClick={() => { if (custom.trim()) { toggleWindow(custom.trim()); setCustom(""); } }}>Add</button>
          </div>
        </div>
        <div><span className={label}>Transportation or loading notes (optional)</span><input data-testid="listing-transport-input" value={f.transport_notes} onChange={set("transport_notes")} placeholder="e.g. Needs 2 people and a truck" className={inputCls} /></div>
      </section>
      <button data-testid="listing-submit-btn" disabled={busy} className={btnPrimary + " w-full h-12"}>{busy ? "Saving…" : id ? "Save changes" : "Post listing"}</button>
    </form>
  );
}
